import type { OrganizationMemoryDTO } from '@eriko/shared-types';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Bot, User, X } from 'lucide-react';
import { useState } from 'react';
import {
  useCreateMemory,
  useDeleteMemory,
  useMemoryList,
} from '../../../hooks/useMemory';

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMs = now - d.getTime();
  const day = 86400000;
  if (diffMs < day) return 'today';
  if (diffMs < 2 * day) return 'yesterday';
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)} days ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function MemoryTab() {
  const { data, isLoading } = useMemoryList();
  const create = useCreateMemory();
  const remove = useDeleteMemory();
  const [draft, setDraft] = useState('');

  const handleAdd = () => {
    const v = draft.trim();
    if (!v) return;
    create.mutate(v, {
      onSuccess: () => setDraft(''),
    });
  };

  const facts = data ?? [];

  return (
    <div className="max-w-3xl">
      <header className="mb-5">
        <h2 className="text-base font-semibold text-surface-900">What Eriko remembers</h2>
        <p className="text-xs text-surface-500 mt-1">
          Things Eriko remembers about your business. Delete anything that&apos;s out of date.
        </p>
      </header>

      <div className="mb-5 flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAdd();
          }}
          placeholder="e.g. My fiscal year ends March 31"
          maxLength={200}
          className="flex-1 rounded-md border border-surface-300 bg-surface-0 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-surface-500"
          disabled={create.isPending}
        />
        <Button size="sm" onClick={handleAdd} disabled={!draft.trim() || create.isPending}>
          {create.isPending ? 'Adding…' : 'Add'}
        </Button>
      </div>

      {isLoading && <p className="text-xs text-surface-500">Loading…</p>}

      {!isLoading && facts.length === 0 && (
        <div className="rounded-lg border border-dashed border-surface-300 px-6 py-10 text-center">
          <p className="text-sm text-surface-700 font-medium">Nothing stored yet.</p>
          <p className="text-xs text-surface-500 mt-1 max-w-sm mx-auto">
            Eriko will remember things you tell it in chat — fiscal year, key people,
            categorization preferences. You can also add them manually above.
          </p>
        </div>
      )}

      {!isLoading && facts.length > 0 && (
        <ul className="divide-y divide-surface-200 border border-surface-200 rounded-lg bg-surface-0">
          {facts.map((f: OrganizationMemoryDTO) => (
            <li key={f.id} className="flex items-start gap-3 px-4 py-3">
              {f.source === 'AI_INFERRED' ? (
                <Bot className="w-3.5 h-3.5 text-surface-500 mt-0.5 shrink-0" strokeWidth={1.8} />
              ) : (
                <User className="w-3.5 h-3.5 text-surface-500 mt-0.5 shrink-0" strokeWidth={1.8} />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-surface-900">{f.content}</p>
                <p className="text-[10px] text-surface-400 mt-0.5">
                  {f.source === 'AI_INFERRED' ? 'Eriko saved' : 'You added'} ·{' '}
                  {relativeDate(f.createdAt)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-surface-400 hover:text-neg shrink-0"
                onClick={() => remove.mutate(f.id)}
                disabled={remove.isPending}
                title="Delete"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
